import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/CustomerSubscriptionsService';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

// Module-scoped global filter state (shared across components)
let globalFilters: Record<string, string> = { status: 'Active' };
const filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useCustomerSubscriptionsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return { filters, handleSetFilters, handleClearFilters };
};

export const useCustomerSubscriptions = (context: 'customer' | 'reports' = 'customer') => {
    const queryClient = useQueryClient();
    const { filters: userFilters } = useCustomerSubscriptionsFilter();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const [selection] = useState<Record<string, boolean>>({});
    const { id: customerId } = useParams();

    const sort = { column: 'created', direction: 'desc' as const };

    // Merge contextual filter (customerId from URL) with user-applied filters
    const mergedFilter: Record<string, string> = {
        ...userFilters,
        ...((context === 'customer' && customerId) ? { customerId } : {})
    };

    const customerSubscriptionsQuery = useInfiniteQuery({
        queryKey: ['customer-subscriptions', keyword, mergedFilter, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            fetchRecords({ limit, sort, filter: mergedFilter, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Customer Subscription deleted');
            queryClient.invalidateQueries({ queryKey: ['customer-subscriptions'] });
        },
        onError: notifyError
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({ queryKey: ['customer-subscriptions'] });
    };

    const fetchMoreRecords = () => {
        if (customerSubscriptionsQuery.hasNextPage) {
            customerSubscriptionsQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = customerSubscriptionsQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: customerSubscriptionsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: customerSubscriptionsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        fetchMoreRecords,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};
